import { useCallback, useEffect, useRef } from "react";
import { useData } from "@hooks/useData";
import { iReportProPublication } from "@models/ReportProPublication";
import { components } from "@apiSchema";
import http from "@core/fetch";
import apiConfig from "@apiConfig";

type Data = {
    items: iReportProPublication[];
};

const DEFAULT_DATA: Data = {
    items: [],
};

const REQUEST_TYPE: keyof typeof apiConfig = 'get category profile macs';
type MacItem = components['schemas']['MacSearchResultShort'];
type Response = MacItem[];

function mapMac(item: MacItem): iReportProPublication {
    return {
        id: item.id ?? 0,
        title: item.title ?? '',
        thumbnail: item.imageUrl ?? '',
        createdDate: item.modifyDate ?? '',
    } as iReportProPublication;
}

async function getCategoryProfileMacs(name: string, signal?: AbortSignal): Promise<Data> {
    if (!name) {
        return DEFAULT_DATA;
    }

    try {
        const response: Response = await http.get(REQUEST_TYPE, { searchText: name, take: 10 }, { signal });

        const items = response.map(mapMac);

        return { items };
    } catch (err) {
        console.error('Fetch error:', REQUEST_TYPE, err);
        return DEFAULT_DATA;
    }
}

export function useCategoryProfileMacs(name: string) {
    const abortRef = useRef<AbortController | null>(null);

    const fetchFn = useCallback((categoryName: string) => {
        abortRef.current?.abort();

        if (!categoryName) {
            return getCategoryProfileMacs(categoryName);
        }

        const controller = new AbortController();
        abortRef.current = controller;

        return getCategoryProfileMacs(categoryName, controller.signal);
    }, []);

    useEffect(() => () => abortRef.current?.abort(), []);

    return useData(DEFAULT_DATA, fetchFn, name);
}
